<template>
  <div class="api-keys-settings">
    <div class="api-keys-settings__header">
      <div class="api-keys-settings__intro">
        <h3>{{ $t('settings.api_keys.title') }}</h3>
        <p class="api-keys-settings__description">
          {{ $t('settings.api_keys.description') }}
        </p>
      </div>
      <Btn
        :text="$t('settings.api_keys.generate')"
        variant="primary"
        @click="openCreateModal"
      />
    </div>

    <div v-if="loading" class="api-keys-settings__loading">
      <LoadingSpinner size="sm" />
      <span>{{ $t('settings.api_keys.loading') }}</span>
    </div>

    <EmptyState
      v-else-if="keys.length === 0"
      icon="integrations"
      :title="$t('settings.api_keys.empty_title')"
      :description="$t('settings.api_keys.empty_description')"
      size="sm"
    />

    <ul v-else class="api-keys-settings__list">
      <li
        v-for="key in keys"
        :key="key.id"
        class="api-keys-settings__item"
        :class="{ 'api-keys-settings__item--revoked': !!key.revoked_at }"
      >
        <p class="api-keys-settings__meta">
          {{ $t('settings.api_keys.created_at') }}: {{ formatDate(key.created_at) }}
          <template v-if="key.last_used_at">
            · {{ $t('settings.api_keys.last_used_at') }}:
            {{ formatDate(key.last_used_at) }}
          </template>
        </p>

        <div class="api-keys-settings__title-row">
          <h4 class="api-keys-settings__name">{{ key.name }}</h4>
          <span
            class="api-keys-settings__badge"
            :class="
              key.revoked_at
                ? 'api-keys-settings__badge--revoked'
                : 'api-keys-settings__badge--active'
            "
          >
            {{
              key.revoked_at
                ? $t('settings.api_keys.status_revoked')
                : $t('settings.api_keys.status_active')
            }}
          </span>
        </div>

        <div class="api-keys-settings__key-row">
          <div class="api-keys-settings__key-field">
            {{ isRevealed(key.id) ? displayKey(key) : maskedKey(key) }}
          </div>
          <div class="api-keys-settings__key-actions">
            <button
              type="button"
              class="api-keys-settings__icon-btn"
              :title="$t('settings.api_keys.toggle_visibility')"
              @click="toggleReveal(key.id)"
            >
              <BaseIcon name="eye" :size="16" />
            </button>
            <button
              v-if="!key.revoked_at"
              type="button"
              class="api-keys-settings__icon-btn api-keys-settings__icon-btn--danger"
              :title="$t('settings.api_keys.revoke')"
              :disabled="revokingId === key.id"
              @click="confirmRevoke(key)"
            >
              <BaseIcon name="trash" :size="16" />
            </button>
          </div>
        </div>
      </li>
    </ul>

    <div class="api-keys-settings__toggle">
      <ToggleInput
        id="include-revoked-api-keys"
        v-model="includeRevoked"
        :label="$t('settings.api_keys.show_revoked')"
        @update:modelValue="fetchKeys"
      />
    </div>
  </div>
</template>

<script>
  import { ref, watch, getCurrentInstance } from 'vue';
  import dayjs from 'dayjs';
  import Btn from '@/components/partials/Btn.vue';
  import EmptyState from '@/components/partials/EmptyState.vue';
  import LoadingSpinner from '@/components/partials/LoadingSpinner.vue';
  import ToggleInput from '@/components/inputs/ToggleInput.vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import CreateApiKeyModal from '@/components/settings/CreateApiKeyModal.vue';
  import OrganizationService from '@/services/organizationService';
  import { useToastStore } from '@/store/toastStore';
  import { useUIStore } from '@/store/uiStore';

  export default {
    name: 'OrganizationApiKeysSettings',
    components: {
      Btn,
      EmptyState,
      LoadingSpinner,
      ToggleInput,
      BaseIcon,
    },
    props: {
      organizationId: {
        type: String,
        required: true,
      },
    },
    setup(props) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;
      const toastStore = useToastStore();
      const uiStore = useUIStore();
      const organizationService = new OrganizationService();

      const keys = ref([]);
      const loading = ref(false);
      const revokingId = ref(null);
      const includeRevoked = ref(false);
      const revealedIds = ref({});

      const formatDate = (value) => {
        if (!value) return '—';
        return dayjs(value).format('DD MMM YYYY');
      };

      const displayKey = (key) => `${key.key_prefix}${'•'.repeat(20)}`;
      const maskedKey = () => '•'.repeat(28);
      const isRevealed = (id) => !!revealedIds.value[id];

      const toggleReveal = (id) => {
        revealedIds.value = {
          ...revealedIds.value,
          [id]: !revealedIds.value[id],
        };
      };

      const fetchKeys = async () => {
        if (!props.organizationId) return;
        loading.value = true;
        try {
          keys.value = await organizationService.getApiKeys(props.organizationId, {
            includeRevoked: includeRevoked.value,
          });
        } catch (error) {
          console.error('Failed to fetch API keys:', error);
          toastStore.addToast({
            type: 'error',
            message: error.response?.data?.error || $t('settings.api_keys.fetch_failed'),
          });
        } finally {
          loading.value = false;
        }
      };

      const openCreateModal = () => {
        const modalId = uiStore.addModal(CreateApiKeyModal, {
          title: $t('settings.api_keys.create_modal_title'),
          size: 'sm',
          organizationId: props.organizationId,
          onCreated: () => {
            fetchKeys();
          },
        });
        return modalId;
      };

      const confirmRevoke = (key) => {
        const modalId = uiStore.addModal(false, {
          title: $t('settings.api_keys.revoke_title'),
          description: $t('settings.api_keys.revoke_description', { name: key.name }),
          status: 'danger',
          submitText: $t('settings.api_keys.revoke'),
          submitVariant: 'danger',
          cancelText: $t('settings.api_keys.cancel'),
          onSubmit: async () => {
            uiStore.closeModal(modalId);
            revokingId.value = key.id;
            try {
              await organizationService.revokeApiKey(props.organizationId, key.id);
              await fetchKeys();
              toastStore.addToast({
                type: 'success',
                message: $t('settings.api_keys.revoke_success'),
              });
            } catch (error) {
              console.error('Failed to revoke API key:', error);
              toastStore.addToast({
                type: 'error',
                message: error.response?.data?.error || $t('settings.api_keys.revoke_failed'),
              });
            } finally {
              revokingId.value = null;
            }
          },
          onCancel: () => uiStore.closeModal(modalId),
        });
      };

      watch(
        () => props.organizationId,
        () => {
          revealedIds.value = {};
          fetchKeys();
        },
        { immediate: true }
      );

      return {
        keys,
        loading,
        revokingId,
        includeRevoked,
        formatDate,
        displayKey,
        maskedKey,
        isRevealed,
        toggleReveal,
        fetchKeys,
        openCreateModal,
        confirmRevoke,
      };
    },
  };
</script>

<style scoped lang="scss">
  .api-keys-settings {
    &__header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 1rem;
      margin-bottom: 1.5rem;

      @include sm-down {
        flex-direction: column;
        align-items: stretch;
      }
    }

    &__intro {
      min-width: 0;

      h3 {
        margin: 0 0 0.375rem;
        font-size: 1.125rem;
        font-weight: 600;
        color: var(--slate-900);
      }
    }

    &__description {
      margin: 0;
      max-width: 36rem;
      font-size: 0.875rem;
      color: var(--slate-500);
      line-height: 1.45;
    }

    &__loading {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      padding: 1.5rem 0;
      color: var(--slate-500);
      font-size: 0.875rem;
    }

    &__list {
      list-style: none;
      margin: 0;
      padding: 0;
      max-width: 720px;
      display: flex;
      flex-direction: column;
    }

    &__item {
      padding: 1.25rem 0;
      border-bottom: 1px solid var(--slate-200);

      &:first-child {
        padding-top: 0;
      }

      &--revoked {
        opacity: 0.72;
      }
    }

    &__meta {
      margin: 0 0 0.375rem;
      font-size: 0.75rem;
      color: var(--slate-400);
    }

    &__title-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 0.75rem;
      margin-bottom: 0.625rem;
    }

    &__name {
      margin: 0;
      font-size: 0.9375rem;
      font-weight: 600;
      color: var(--slate-800);
    }

    &__badge {
      padding: 0.2rem 0.625rem;
      border-radius: 9999px;
      font-size: 0.6875rem;
      font-weight: 600;
      flex-shrink: 0;

      &--active {
        background: var(--green-100);
        color: var(--green-700);
      }

      &--revoked {
        background: var(--slate-100);
        color: var(--slate-600);
      }
    }

    &__key-row {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    &__key-field {
      flex: 1;
      min-width: 0;
      padding: 0.625rem 0.875rem;
      border: 1px solid var(--slate-200);
      border-radius: 0.5rem;
      background: var(--slate-50);
      font-family: 'SF Mono', 'Fira Code', Menlo, Consolas, monospace;
      font-size: 0.8125rem;
      color: var(--slate-700);
      letter-spacing: 0.02em;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    &__key-actions {
      display: flex;
      align-items: center;
      gap: 0.125rem;
      flex-shrink: 0;
    }

    &__icon-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 34px;
      height: 34px;
      border: none;
      border-radius: 0.375rem;
      background: transparent;
      color: var(--slate-500);
      cursor: pointer;

      @include hover {
        background: var(--slate-100);
        color: var(--slate-700);
      }

      &:disabled {
        opacity: 0.5;
        cursor: default;
      }

      &--danger {
        color: var(--red-500);

        @include hover {
          background: var(--red-50);
          color: var(--red-600);
        }
      }
    }

    &__toggle {
      margin-top: 1.25rem;
      max-width: 320px;
    }
  }
</style>
